import {
  type Issue,
  issueListFiltersSchema,
  sortDirectionSchema,
} from "@qualtio-workhub/contracts";
import { defaultParseSearch, defaultStringifySearch } from "@tanstack/react-router";
import type { IssueFilters } from "../../shared/api/client";

export const issueViews = ["all", "mine", "open", "blocked", "agents"] as const;
export type IssueView = (typeof issueViews)[number];
export type IssueSort = "updated" | "priority";
export type IssueListSearch = IssueFilters & {
  view?: IssueView;
  sort?: IssueSort;
  direction?: "asc" | "desc";
};

export function readIssueListSearch(search: string): IssueListSearch {
  const params = defaultParseSearch(search) as Record<string, unknown>;
  const result: Record<string, string> = {};
  for (const [key, schema] of Object.entries(issueListFiltersSchema.shape)) {
    const value: unknown = params[key];
    const parsed = schema.safeParse(
      typeof value === "number" || typeof value === "boolean" || value === null
        ? String(value)
        : value,
    );
    if (parsed.success && parsed.data?.trim()) result[key] = parsed.data;
  }
  const view = params["view"];
  if (issueViews.includes(view as IssueView) && view !== "all") result["view"] = view as IssueView;
  if (params["sort"] === "priority") result["sort"] = "priority";
  const direction = sortDirectionSchema.safeParse(params["direction"]);
  if (direction.success && direction.data === "asc") result["direction"] = "asc";
  return result as IssueListSearch;
}

export function issueListSearchParams(search: IssueListSearch): string {
  const normalized = readIssueListSearch(defaultStringifySearch(search));
  return defaultStringifySearch(normalized).replace(/^\?/, "");
}

export function issueListHref(search: IssueListSearch): string {
  const params = issueListSearchParams(search);
  return params ? `/issues?${params}` : "/issues";
}

const priorities = { none: 0, low: 1, medium: 2, high: 3, urgent: 4 };

export function selectIssueView(
  issues: Issue[],
  view: IssueView,
  userId: string | undefined,
): Issue[] {
  return issues.filter((issue) => {
    if (view === "mine") return issue.assigneeUserId === userId;
    if (view === "open") return issue.status !== "done" && issue.status !== "canceled";
    if (view === "blocked") return issue.status === "blocked";
    if (view === "agents") return issue.assigneeAgentId !== null;
    return true;
  });
}

export function sortIssueList(
  issues: Issue[],
  sort: IssueSort,
  direction: "asc" | "desc",
): Issue[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...issues].sort((left, right) => {
    const byUpdated = Date.parse(left.updatedAt) - Date.parse(right.updatedAt);
    const primary =
      sort === "priority" ? priorities[left.priority] - priorities[right.priority] : byUpdated;
    return (
      primary * sign ||
      -byUpdated ||
      left.key.localeCompare(right.key, undefined, { numeric: true })
    );
  });
}
